import cssImports from './imports.json' with { type: 'json' };
import { defaultBase } from './links/constants';
import { createFilter } from './links/create-filter';
import { getMediaQuery } from './links/get-media-query';
import type { LinkProps, LinkPropsList, LinksFunction } from './links/types';

const fontPreloadUrls = [
  'https://www.volvocars.com/static/shared/fonts/volvo-centum/v1/volvo-centum.woff2',
];

export const links: LinksFunction = ({ base = defaultBase, filter } = {}) => {
  const baseHref = typeof base === 'string' ? base : base?.href;
  const normalizedBase =
    baseHref && !baseHref.endsWith('/') ? `${baseHref}/` : baseHref;
  const cssLinkProps = Object.entries(cssImports)
    .filter(createFilter(filter))
    .flatMap<LinkProps>(([name, filename]) => {
      const props: LinkProps = {
        href: normalizedBase + filename,
        rel: 'stylesheet',
        crossOrigin: 'anonymous',
      };
      const media = getMediaQuery(name);
      if (media) {
        props.media = media;
      }

      const preloadProps: LinkProps = {
        ...props,
        rel: 'preload',
        as: 'style',
      };

      props['data-volvo-css-name'] = name;

      if (filter?.preload === false) {
        return props;
      }
      return [preloadProps, props] as const;
    })
    .sort((a, b) =>
      a.rel === 'preload' && b.rel === 'preload'
        ? 0
        : a.rel === 'preload'
          ? -1
          : 1,
    );

  const fontPreloads: LinkProps[] =
    filter?.fonts === false || filter?.preload === false
      ? []
      : fontPreloadUrls.map((href) => ({
          rel: 'preload',
          href,
          as: 'font',
          type: 'font/woff2',
          crossOrigin: 'anonymous',
        }));
  const linkProps: LinkPropsList = [...fontPreloads, ...cssLinkProps];

  linkProps.toString = function () {
    let html = '';
    for (const link of this) {
      const attrs = Object.entries(link)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
      html += `<link ${attrs} />\n`;
    }
    return html;
  };

  return linkProps;
};

export type { LinkProps, LinksFunction };
